// src/api/tasaCompra.js - API para manejo de tasas de compra
import { apiFetch } from './index.js';
import { obtenerFuentePorId } from './fuenteTasa.js';

/**
 * Obtener todas las tasas de compra
 * @param {string} estado - 'all', 'activas', 'inactivas'
 * @returns {Promise} Lista de tasas de compra
 */
export const obtenerTasasCompra = async (estado = 'all') => {
  try {
    console.log('📋 Obteniendo tasas de compra...', { estado });
    
    let endpoint = '/api/TasaCambioCompra';
    
    if (estado === 'activas') {
      endpoint = '/api/TasaCambioCompra/activas';
    }
    
    const response = await apiFetch(endpoint);
    
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Tasas de compra obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo tasas de compra:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener tasa de compra actual
 * @returns {Promise} Tasa de compra actual
 */
export const obtenerTasaCompraActual = async () => {
  try {
    console.log('💰 Obteniendo tasa de compra actual...');
    
    const response = await apiFetch('/api/TasaCambioCompra/actual');
    
    return {
      success: true,
      data: response,
      message: 'Tasa de compra actual obtenida exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo tasa de compra actual:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener una tasa de compra por ID
 * @param {number} id - ID de la tasa
 * @returns {Promise} Datos de la tasa
 */
export const obtenerTasaCompraPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de tasa es requerido');
    }
    
    console.log(`🔍 Obteniendo tasa de compra ID: ${id}`);
    
    const response = await apiFetch(`/api/TasaCambioCompra/${id}`);
    
    return {
      success: true,
      data: response,
      message: 'Tasa de compra obtenida exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo tasa de compra ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener tasas de compra por fuente
 * @param {number} idFuente - ID de la fuente
 * @returns {Promise} Tasas de la fuente
 */
export const obtenerTasasCompraPorFuente = async (idFuente) => {
  try {
    if (!idFuente) {
      throw new Error('ID de fuente es requerido');
    }
    
    console.log(`🌐 Obteniendo tasas de compra de fuente ID: ${idFuente}`);
    
    const response = await apiFetch(`/api/TasaCambioCompra/fuente/${idFuente}`);
    
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Tasas de compra por fuente obtenidas'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo tasas de compra por fuente ${idFuente}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener historial de tasas de compra por fuente
 * @param {number} idFuente - ID de la fuente
 * @returns {Promise} Historial de tasas
 */
export const obtenerHistorialTasasCompra = async (idFuente) => {
  try {
    if (!idFuente) {
      throw new Error('ID de fuente es requerido');
    }
    
    console.log(`📊 Obteniendo historial de tasas de compra de fuente ID: ${idFuente}`);
    
    const response = await apiFetch(`/api/TasaCambioCompra/fuente/${idFuente}/historial`);
    
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Historial de tasas de compra obtenido'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo historial de tasas de compra ${idFuente}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener tasas de compra por fecha
 * @param {string} fecha - Fecha específica (YYYY-MM-DD)
 * @returns {Promise} Tasas de esa fecha
 */
export const obtenerTasasCompraPorFecha = async (fecha) => {
  try {
    if (!fecha) {
      throw new Error('Fecha es requerida');
    }
    
    console.log(`📅 Obteniendo tasas de compra para fecha: ${fecha}`);
    
    const response = await apiFetch(`/api/TasaCambioCompra/fecha/${encodeURIComponent(fecha)}`);
    
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Tasas de compra por fecha obtenidas'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo tasas de compra por fecha ${fecha}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Buscar tasas de compra
 * @param {Object} criterios - Criterios de búsqueda
 * @returns {Promise} Resultados de la búsqueda
 */
export const buscarTasasCompra = async (criterios) => {
  try {
    console.log('🔎 Buscando tasas de compra:', criterios);
    
    const response = await apiFetch('/api/TasaCambioCompra/search', {
      method: 'POST',
      body: JSON.stringify(criterios || {})
    });
    
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Búsqueda completada'
    };
    
  } catch (error) {
    console.error('❌ Error buscando tasas de compra:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener tasa de compra con relaciones
 * @param {number} id - ID de la tasa
 * @returns {Promise} Tasa con relaciones
 */
export const obtenerTasaCompraConRelaciones = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de tasa es requerido');
    }
    
    console.log(`🔍 Obteniendo tasa de compra con relaciones ID: ${id}`);
    
    const response = await apiFetch(`/api/TasaCambioCompra/${id}/conRelaciones`);
    
    return {
      success: true,
      data: response,
      message: 'Tasa de compra con relaciones obtenida'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo tasa de compra con relaciones ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Verificar si existe una tasa de compra por ID
 * @param {number} id - ID de la tasa
 * @returns {Promise} Resultado de la verificación
 */
export const verificarTasaCompraExiste = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de tasa es requerido');
    }
    
    console.log(`🔎 Verificando existencia de tasa de compra ID: ${id}`);
    
    const response = await apiFetch(`/api/TasaCambioCompra/${id}/exists`);
    
    return {
      success: true,
      existe: response?.existe || false,
      data: response,
      message: 'Verificación completada'
    };
    
  } catch (error) {
    console.error(`❌ Error verificando tasa de compra ${id}:`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Validar una tasa de compra
 * @param {Object} tasaData - Datos a validar
 * @returns {Promise} Resultado de la validación
 */
export const validarTasaCompra = async (tasaData) => {
  try {
    console.log('✅ Validando tasa de compra:', tasaData);
    
    const response = await apiFetch('/api/TasaCambioCompra/validar', {
      method: 'POST',
      body: JSON.stringify(tasaData || {})
    });
    
    return {
      success: true,
      valido: true,
      data: response,
      message: 'Validación completada'
    };
    
  } catch (error) {
    console.error('❌ Error validando tasa de compra:', error);
    return {
      success: false,
      valido: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Crear una nueva tasa de compra
 * @param {Object} tasaData - Datos de la tasa
 * @returns {Promise} Tasa creada
 */
export const crearTasaCompra = async (tasaData) => {
  try {
    // Validaciones
    if (!tasaData.precio_usdt_bs || tasaData.precio_usdt_bs <= 0) {
      throw new Error('El precio en Bs es requerido y debe ser mayor a 0');
    }
    if (!tasaData.idFuenteTasa) {
      throw new Error('La fuente de tasa es requerida');
    }
    
    console.log('➕ Creando nueva tasa de compra:', tasaData);
    
    // Verificar que la fuente existe
    const fuenteResponse = await obtenerFuentePorId(tasaData.idFuenteTasa);
    if (!fuenteResponse.success) {
      throw new Error('La fuente especificada no existe');
    }
    
    const response = await apiFetch('/api/TasaCambioCompra', {
      method: 'POST',
      body: JSON.stringify({
        fecha: tasaData.fecha || new Date().toISOString(),
        precio_usdt_bs: parseFloat(tasaData.precio_usdt_bs),
        precio_usdt_usd: tasaData.precio_usdt_usd || 1,
        idFuenteTasa: parseInt(tasaData.idFuenteTasa),
        activa: tasaData.activa !== undefined ? tasaData.activa : true,
        observaciones: tasaData.observaciones || null,
        idUsuario: tasaData.idUsuario || null
      })
    });
    
    return {
      success: true,
      data: response,
      message: 'Tasa de compra creada exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando tasa de compra:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Actualizar una tasa de compra existente
 * @param {number} id - ID de la tasa
 * @param {Object} tasaData - Datos a actualizar
 * @returns {Promise} Tasa actualizada
 */
export const actualizarTasaCompra = async (id, tasaData) => {
  try {
    if (!id) {
      throw new Error('ID de tasa es requerido');
    }
    
    if (!tasaData.precio_usdt_bs || tasaData.precio_usdt_bs <= 0) {
      throw new Error('El precio en Bs es requerido y debe ser mayor a 0');
    }
    if (!tasaData.idFuenteTasa) {
      throw new Error('La fuente de tasa es requerida');
    }
    
    console.log(`✏️ Actualizando tasa de compra ID: ${id}`, tasaData);
    
    const updateData = {
      precio_usdt_bs: parseFloat(tasaData.precio_usdt_bs),
      precio_usdt_usd: tasaData.precio_usdt_usd || 1,
      idFuenteTasa: parseInt(tasaData.idFuenteTasa),
      activa: tasaData.activa !== undefined ? tasaData.activa : true,
      observaciones: tasaData.observaciones || null
    };
    
    if (tasaData.idUsuario) {
      updateData.idUsuario = parseInt(tasaData.idUsuario);
    }
    
    const response = await apiFetch(`/api/TasaCambioCompra/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
    
    return {
      success: true,
      data: response,
      message: 'Tasa de compra actualizada exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando tasa de compra ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Eliminar una tasa de compra
 * @param {number} id - ID de la tasa a eliminar
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarTasaCompra = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de tasa es requerido');
    }
    
    console.log(`🗑️ Eliminando tasa de compra ID: ${id}`);
    
    const response = await apiFetch(`/api/TasaCambioCompra/${id}`, {
      method: 'DELETE'
    });
    
    return {
      success: true,
      data: response,
      message: 'Tasa de compra eliminada exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error eliminando tasa de compra ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Cambiar estado de una tasa de compra
 * @param {number} id - ID de la tasa
 * @param {boolean} activa - Nuevo estado
 * @returns {Promise} Resultado del cambio
 */
export const cambiarEstadoTasaCompra = async (id, activa) => {
  try {
    if (!id || activa === undefined) {
      throw new Error('ID y estado son requeridos');
    }
    
    console.log(`🔄 Cambiando estado de tasa de compra ID: ${id} a ${activa ? 'activa' : 'inactiva'}`);
    
    const response = await apiFetch(`/api/TasaCambioCompra/${id}/estado`, {
      method: 'PATCH',
      body: JSON.stringify({ activa })
    });
    
    return {
      success: true,
      data: response,
      message: 'Estado de tasa de compra actualizado'
    };
    
  } catch (error) {
    console.error(`❌ Error cambiando estado de tasa de compra ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Activar una tasa de compra
 * @param {number} id - ID de la tasa
 * @returns {Promise} Resultado de la activación
 */
export const activarTasaCompra = async (id) => {
  return await cambiarEstadoTasaCompra(id, true);
};

/**
 * Desactivar una tasa de compra
 * @param {number} id - ID de la tasa
 * @returns {Promise} Resultado de la desactivación
 */
export const desactivarTasaCompra = async (id) => {
  return await cambiarEstadoTasaCompra(id, false);
};

/**
 * Convertir moneda usando tasa de compra
 * @param {number} monto - Monto a convertir
 * @param {string} direccion - 'usd_to_bs' o 'bs_to_usd'
 * @returns {Promise} Resultado de la conversión
 */
export const convertirMonedaCompra = async (monto, direccion = 'usd_to_bs') => {
  try {
    if (!monto || monto <= 0) {
      throw new Error('Monto válido es requerido');
    }
    
    console.log(`💱 Convirtiendo ${monto} (${direccion}) con tasa de compra`);
    
    const response = await apiFetch('/api/TasaCambioCompra/convertir', {
      method: 'POST',
      body: JSON.stringify({
        monto: parseFloat(monto),
        tipoOperacion: direccion
      })
    });
    
    return {
      success: true,
      data: response,
      message: 'Conversión realizada exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error convirtiendo moneda:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Convertir moneda con tasa personalizada
 * @param {number} monto - Monto a convertir
 * @param {number} tasa - Tasa personalizada
 * @param {string} direccion - 'usd_to_bs' o 'bs_to_usd'
 * @returns {Promise} Resultado de la conversión
 */
export const convertirMonedaCompraConTasa = async (monto, tasa, direccion = 'usd_to_bs') => {
  try {
    if (!monto || monto <= 0) {
      throw new Error('Monto válido es requerido');
    }
    if (!tasa || tasa <= 0) {
      throw new Error('Tasa válida es requerida');
    }
    
    console.log(`💱 Convirtiendo ${monto} con tasa personalizada ${tasa} (${direccion})`);
    
    const response = await apiFetch('/api/TasaCambioCompra/convertir-con-tasa', {
      method: 'POST',
      body: JSON.stringify({
        monto: parseFloat(monto),
        tasaPersonalizada: parseFloat(tasa),
        tipoOperacion: direccion
      })
    });
    
    return {
      success: true,
      data: response,
      message: 'Conversión personalizada realizada'
    };
    
  } catch (error) {
    console.error('❌ Error convirtiendo moneda con tasa personalizada:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener reporte de tasas de compra
 * @param {Object} parametros - Parámetros del reporte
 * @returns {Promise} Reporte generado
 */
export const obtenerReporteTasasCompra = async (parametros = {}) => {
  try {
    console.log('📊 Obteniendo reporte de tasas de compra:', parametros);
    
    const queryParams = new URLSearchParams(parametros).toString();
    const url = `/api/TasaCambioCompra/reporte${queryParams ? `?${queryParams}` : ''}`;
    
    const response = await apiFetch(url);
    
    return {
      success: true,
      data: response,
      message: 'Reporte obtenido exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo reporte de tasas de compra:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// ==================== FUNCIONES AUXILIARES ====================

/**
 * Formatear precio de tasa de compra
 * @param {number} precio - Precio a formatear
 * @returns {string} Precio formateado
 */
export const formatearPrecioCompra = (precio) => {
  if (precio === null || precio === undefined) return 'N/A';
  return `${precio.toFixed(2)} Bs/USDT`;
};

/**
 * Calcular monto en Bs a partir de USDT
 * @param {number} usdt - Cantidad de USDT
 * @param {number} tasa - Tasa de compra
 * @returns {number} Monto en Bs
 */
export const calcularBsDesdeUsdt = (usdt, tasa) => {
  if (!usdt || !tasa) return 0;
  return usdt * tasa;
};

/**
 * Calcular monto en USDT a partir de Bs
 * @param {number} bs - Cantidad en Bs
 * @param {number} tasa - Tasa de compra
 * @returns {number} Monto en USDT
 */
export const calcularUsdtDesdeBs = (bs, tasa) => {
  if (!bs || !tasa) return 0;
  return bs / tasa;
};

export default {
  obtenerTasasCompra,
  obtenerTasaCompraActual,
  obtenerTasaCompraPorId,
  obtenerTasasCompraPorFuente,
  obtenerHistorialTasasCompra,
  obtenerTasasCompraPorFecha,
  buscarTasasCompra,
  obtenerTasaCompraConRelaciones,
  verificarTasaCompraExiste,
  validarTasaCompra,
  crearTasaCompra,
  actualizarTasaCompra,
  eliminarTasaCompra,
  activarTasaCompra,
  desactivarTasaCompra,
  cambiarEstadoTasaCompra,
  convertirMonedaCompra,
  convertirMonedaCompraConTasa,
  obtenerReporteTasasCompra,
  formatearPrecioCompra,
  calcularBsDesdeUsdt,
  calcularUsdtDesdeBs
};